import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { IconType } from 'react-icons';
import {
  FiActivity,
  FiBookOpen,
  FiCalendar,
  FiFileText,
  FiGrid,
  FiLogOut,
  FiMapPin,
  FiRepeat,
  FiUsers,
} from 'react-icons/fi';
import { authService } from '../services/authService';
import {
  clientPortalService,
  type ClientPortalContext,
  type ClientPortalScope,
} from '../services/clientPortalService';
import { useI18n } from '../i18n';
import LanguageSwitcher from './LanguageSwitcher';
import ClientOverviewPage from '../pages/clientPortal/ClientOverviewPage';
import ClientLiveOpsPage from '../pages/clientPortal/ClientLiveOpsPage';
import ClientReportsPage from '../pages/clientPortal/ClientReportsPage';
import ClientToursPage from '../pages/clientPortal/ClientToursPage';
import ClientPostOrdersPage from '../pages/clientPortal/ClientPostOrdersPage';
import ClientSchedulePage from '../pages/clientPortal/ClientSchedulePage';
import ClientTeamPage from '../pages/clientPortal/ClientTeamPage';
import './ClientPortalShell.css';

export type ClientPortalSection =
  | 'overview'
  | 'live'
  | 'reports'
  | 'tours'
  | 'orders'
  | 'schedule'
  | 'team';

interface NavItem {
  section: ClientPortalSection;
  label: string;
  summary: string;
  icon: IconType;
  /** Shown when the user holds at least one of these scopes. */
  anyOf?: ClientPortalScope[];
  primaryOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { section: 'overview', label: 'Overview', summary: 'Today at a glance', icon: FiGrid },
  {
    section: 'live',
    label: 'Live Operations',
    summary: 'Officers on duty right now',
    icon: FiActivity,
    anyOf: ['live_map', 'active_guards', 'patrol_progress'],
  },
  {
    section: 'reports',
    label: 'Reports',
    summary: 'Incidents, daily logs and visitor records',
    icon: FiFileText,
    anyOf: ['incidents', 'daily_reports', 'visitor_logs', 'patrol_records'],
  },
  {
    section: 'tours',
    label: 'Guard Tours',
    summary: 'Patrol sweeps and checkpoint history',
    icon: FiRepeat,
    anyOf: ['patrol_progress', 'patrol_records'],
  },
  {
    section: 'orders',
    label: 'Post Orders',
    summary: 'Site rules and emergency procedures',
    icon: FiBookOpen,
    anyOf: ['post_orders'],
  },
  {
    section: 'schedule',
    label: 'Schedule',
    summary: 'Coverage calendar and cover requests',
    icon: FiCalendar,
    anyOf: ['schedule', 'service_requests'],
  },
  {
    section: 'team',
    label: 'Portal Users',
    summary: 'Who on your team can see what',
    icon: FiUsers,
    anyOf: ['team'],
    primaryOnly: true,
  },
];

const ALL_SITES = '__all__';

function ClientPortalLoading() {
  return (
    <div className="client-portal client-portal--loading">
      <div className="spinner" />
    </div>
  );
}

export default function ClientPortalShell() {
  const { t } = useI18n();
  const [context, setContext] = useState<ClientPortalContext | null>(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [section, setSection] = useState<ClientPortalSection>('overview');
  const [siteFilter, setSiteFilter] = useState<string>(ALL_SITES);

  const loadContext = useCallback(async () => {
    const data = await clientPortalService.fetchContext();
    setContext(data);
    return data;
  }, []);

  useEffect(() => {
    let cancelled = false;

    void loadContext()
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || t('Failed to load client portal'));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [loadContext, t]);

  const scopes = useMemo(() => new Set(context?.scopes ?? []), [context]);
  const can = useCallback(
    (scope: ClientPortalScope) => scopes.has(scope),
    [scopes],
  );

  const visibleNav = useMemo(() => {
    if (!context) return [];
    return NAV_ITEMS.filter((item) => {
      if (item.primaryOnly && context.user.role !== 'primary') return false;
      if (!item.anyOf) return true;
      return item.anyOf.some((scope) => scopes.has(scope));
    });
  }, [context, scopes]);

  // A member whose permissions change underneath them should never be left
  // staring at a section they can no longer open.
  useEffect(() => {
    if (visibleNav.length === 0) return;
    if (!visibleNav.some((item) => item.section === section)) {
      setSection(visibleNav[0].section);
    }
  }, [visibleNav, section]);

  const handleLogout = async () => {
    await authService.signOut();
    window.location.href = process.env.REACT_APP_MARKETING_URL ?? 'https://odinseye.eu';
  };

  if (isLoading) return <ClientPortalLoading />;

  if (error || !context) {
    return (
      <div className="client-portal client-portal--error">
        <div className="client-portal__error-card">
          <h1>{t('Client portal unavailable')}</h1>
          <p>{error || t('Your account could not be loaded.')}</p>
          <button type="button" className="btn btn--primary" onClick={handleLogout}>
            {t('Sign out')}
          </button>
        </div>
      </div>
    );
  }

  const activeSiteId = siteFilter === ALL_SITES ? null : siteFilter;
  const activeNavItem = visibleNav.find((item) => item.section === section) ?? visibleNav[0] ?? null;
  const initials = (context.user.full_name ?? context.user.email)
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

  const renderSection = () => {
    switch (section) {
      case 'live':
        return <ClientLiveOpsPage context={context} siteId={activeSiteId} can={can} />;
      case 'reports':
        return <ClientReportsPage context={context} siteId={activeSiteId} can={can} />;
      case 'tours':
        return <ClientToursPage context={context} siteId={activeSiteId} can={can} />;
      case 'orders':
        return <ClientPostOrdersPage context={context} siteId={activeSiteId} />;
      case 'schedule':
        return <ClientSchedulePage context={context} siteId={activeSiteId} can={can} />;
      case 'team':
        return <ClientTeamPage context={context} onChanged={loadContext} />;
      case 'overview':
      default:
        return (
          <ClientOverviewPage
            context={context}
            siteId={activeSiteId}
            can={can}
            onNavigate={setSection}
          />
        );
    }
  };

  return (
    <div className="client-portal">
      <aside className="client-portal__sidebar">
        <div className="client-portal__brand">
          <div className="client-portal__brand-mark">O</div>
          <div>
            <div className="client-portal__brand-title">OdinsEye</div>
            <div className="client-portal__brand-subtitle">{t('Client Portal')}</div>
          </div>
        </div>

        <div className="client-portal__identity">
          <div className="client-portal__eyebrow">{t('Client account')}</div>
          <h2>{context.contract.name}</h2>
          <p>{t('Secured by {company}', { company: context.company.name })}</p>
        </div>

        {context.sites.length > 1 ? (
          <div className="client-portal__site-switch">
            <label htmlFor="cp-site-filter">{t('Viewing')}</label>
            <select
              id="cp-site-filter"
              className="form__select"
              value={siteFilter}
              onChange={(event) => setSiteFilter(event.target.value)}
            >
              <option value={ALL_SITES}>{t('All sites ({count})', { count: context.sites.length })}</option>
              {context.sites.map((site) => (
                <option key={site.id} value={site.id}>{site.name}</option>
              ))}
            </select>
          </div>
        ) : null}

        <nav className="client-portal__nav" aria-label={t('Client portal navigation')}>
          {visibleNav.map((item) => {
            const Icon = item.icon;
            const isActive = item.section === section;
            return (
              <button
                key={item.section}
                type="button"
                aria-current={isActive ? 'page' : undefined}
                className={`client-portal__nav-item${isActive ? ' client-portal__nav-item--active' : ''}`}
                onClick={() => setSection(item.section)}
              >
                <Icon aria-hidden="true" />
                <span>{t(item.label)}</span>
              </button>
            );
          })}
        </nav>

        <div className="client-portal__user">
          <div className="client-portal__avatar" aria-hidden="true">{initials}</div>
          <div className="client-portal__user-meta">
            <strong>{context.user.full_name ?? context.user.email}</strong>
            <span>
              {context.user.role === 'primary'
                ? t('Primary contact')
                : context.user.job_title ?? t('Portal user')}
            </span>
          </div>
        </div>

        <div className="client-portal__lang">
          <LanguageSwitcher />
        </div>

        <button type="button" className="client-portal__logout" onClick={handleLogout}>
          <FiLogOut aria-hidden="true" />
          <span>{t('Sign out')}</span>
        </button>
      </aside>

      <main className="client-portal__main">
        <header className="client-portal__topbar">
          <div>
            <div className="client-portal__eyebrow">
              {activeSiteId
                ? context.sites.find((site) => site.id === activeSiteId)?.name ?? t('Site')
                : t('All sites')}
            </div>
            <h1>{t(activeNavItem?.label ?? 'Overview')}</h1>
            <p>{t(activeNavItem?.summary ?? 'Today at a glance')}</p>
          </div>
          <div className="client-portal__topbar-meta">
            <FiMapPin aria-hidden="true" />
            <span>
              {activeSiteId
                ? t('1 of {count} sites', { count: context.sites.length })
                : t('{count} sites under contract', { count: context.sites.length })}
            </span>
          </div>
        </header>

        {renderSection()}
      </main>
    </div>
  );
}
